import React, { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import FeedbackIcon from "@mui/icons-material/Feedback";
import RateReviewIcon from "@mui/icons-material/RateReview";
import TimelineIcon from "@mui/icons-material/Timeline";
import makeStyles from '@mui/styles/makeStyles';
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate } from "react-router-dom";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PeopleIcon from "@mui/icons-material/People";
import Divider from "@mui/material/Divider";
import NotificacionesPanel from "./NotificacionesPanel";
import BandejaEntradaPanel from "./BandejaEntradaPanel";
import MisTareasPanel from "./MisTareasPanel";
import FeedbackBetaDialog from "./FeedbackBetaDialog";
import { usePermisos } from "../utils/usePermisos";

import { notificacionesListarJWT, pasesBandejaCountJWT, feedbackBetaSoyAdminJWT, feedbackBetaNoLeidosJWT } from "../api/endpoints";
import { nodeApiPath } from "../utils/paths";

// Helper: URL de foto del personal desde F:\SIGIF_Archivos\Fotos_Personal
function fotoUrl(idPersonal) {
    if (!idPersonal) return null;
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    return `${nodeApiPath(`personal/${idPersonal}/foto`)}?token=${token}`;
}

const useStyles = makeStyles((theme) => ({
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.light} 100%)`,
    },
    title: {
        fontWeight: "normal",
        fontSize: 20,
        flexGrow: 1,
        //letterSpacing: 2,
        marginLeft: theme.spacing(2),
        lineHeight: 1.3, // Fix para evitar que la "g" se corte
    },
    usuario: {
        marginLeft: theme.spacing(2),
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    avatar: {
        width: 36,
        height: 36,
        border: "2px solid rgba(255, 255, 255, 0.8)",
        fontSize: 14,
        fontWeight: 600,
    },
    iconButton: {
        marginLeft: theme.spacing(1),
    },
}));

const Header = ({
    usuario,
    moduloActivo,
    setModuloActivo,
    onLogout,
    onDrawerToggle,
    darkMode,
    setDarkMode,
}) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { tienePermiso } = usePermisos();
    const puedeAdminPermisos = tienePermiso('ADMINISTRACION DE PERMISOS', 'SELECT');
    const puedePersonal = tienePermiso('ACCESO AL MODULO', 'SELECT', 'PERSONAL');
    // "Mi Actividad" solo aplica a abogados/fiscales/letrados/procuradores.
    // Requiere id_personal real (>0); sin vinculación a Personal no puede tener expedientes.
    const tienePersonalReal = usuario?.id_personal && Number(usuario.id_personal) > 0;
    const esAbogado = !!(tienePersonalReal && usuario?.funcion && (
        usuario.funcion.toLowerCase().includes('abogad') ||
        usuario.funcion.toLowerCase().includes('fiscal') ||
        usuario.funcion.toLowerCase().includes('letrad') ||
        usuario.funcion.toLowerCase().includes('procurad')
    ));

    // Menú usuario
    const [anchorEl, setAnchorEl] = useState(null);

    // Avatar del usuario
    const [avatarSrc, setAvatarSrc] = useState(null);
    const [avatarError, setAvatarError] = useState(false);

    // Notificaciones
    const [notifOpen, setNotifOpen] = useState(false);
    const [noLeidas, setNoLeidas] = useState(0);

    // Bandeja de entrada (pases)
    const [bandejaOpen, setBandejaOpen] = useState(false);
    const [pasesPendientes, setPasesPendientes] = useState(0);

    // Mis Tareas (aprobaciones como director/autoridad)
    const [tareasOpen, setTareasOpen] = useState(false);
    const [tareasCount, setTareasCount] = useState(0);

    // Feedback Beta (botón disponible para todos; admins ven badge + entrada al panel)
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [esFeedbackAdmin, setEsFeedbackAdmin] = useState(false);
    const [feedbackNoLeidos, setFeedbackNoLeidos] = useState(0);

    // Obtener iniciales del nombre
    const getInitials = (nombre) => {
        if (!nombre) return "?";
        const parts = nombre.trim().split(" ");
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return nombre.substring(0, 2).toUpperCase();
    };

    // Capitalizar palabras
    const capitalizeWords = (str) => {
        if (!str) return '';
        return str.toLowerCase().split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    // Cargar foto del usuario
    const [fotoVersion, setFotoVersion] = useState(Date.now());
    useEffect(() => {
        if (usuario?.dni) {
            const photoUrl = `${fotoUrl(usuario.id_personal || usuario.dni)}&v=${fotoVersion}`;
            setAvatarSrc(photoUrl);
            setAvatarError(false);
        }
    }, [usuario, fotoVersion]);

    // Escuchar evento global cuando se actualiza la foto desde Perfil
    useEffect(() => {
        const handler = () => setFotoVersion(Date.now());
        window.addEventListener('sigif-foto-actualizada', handler);
        return () => window.removeEventListener('sigif-foto-actualizada', handler);
    }, []);

    // Permitir abrir la bandeja de pases desde otros componentes (Dashboard card).
    // La bandeja vive aca como Drawer y no tiene ruta propia.
    useEffect(() => {
        const handler = () => setBandejaOpen(true);
        window.addEventListener('sigif-open-bandeja', handler);
        return () => window.removeEventListener('sigif-open-bandeja', handler);
    }, []);

    // Idem para el panel de notificaciones (también vive aca como popover).
    useEffect(() => {
        const handler = () => setNotifOpen(true);
        window.addEventListener('sigif-open-notificaciones', handler);
        return () => window.removeEventListener('sigif-open-notificaciones', handler);
    }, []);

    // Cargar notificaciones no leídas + pases pendientes + tareas
    useEffect(() => {
        cargarNoLeidas();
        cargarPasesPendientes();
        cargarTareas();
        const interval = setInterval(() => {
            cargarNoLeidas();
            cargarPasesPendientes();
            cargarTareas();
        }, 30000); // Cada 30 segundos
        return () => clearInterval(interval);
    }, []);

    // Feedback Beta: chequear si soy admin (una vez) + poll del badge cada 60s
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const r = await feedbackBetaSoyAdminJWT();
                if (cancel) return;
                if (r?.ok && r.esAdmin) {
                    setEsFeedbackAdmin(true);
                    cargarFeedbackNoLeidos();
                    const t = setInterval(cargarFeedbackNoLeidos, 60000);
                    // Cleanup del interval cuando el componente se desmonta
                    return () => clearInterval(t);
                }
            } catch { /* silent */ }
        })();
        return () => { cancel = true; };
        // eslint-disable-next-line
    }, []);

    const cargarFeedbackNoLeidos = async () => {
        try {
            const r = await feedbackBetaNoLeidosJWT();
            if (r?.ok) setFeedbackNoLeidos(Number(r.count || 0));
        } catch { /* silent */ }
    };

    const cargarTareas = async () => {
        try {
            const token = localStorage.getItem("SIGIF_JWT_TOKEN");
            if (!token) return;
            const res = await fetch(nodeApiPath("personal/bandeja/mis-tareas"), {
                headers: { "Authorization": `Bearer ${token}` },
            });
            if (!res.ok) return;
            const data = await res.json();
            if (data?.ok) setTareasCount((data.items || []).length);
        } catch { /* silent */ }
    };

    const cargarNoLeidas = async () => {
        try {
            const response = await notificacionesListarJWT();
            if (response?.ok) {
                setNoLeidas(response.noLeidas || 0);
            }
        } catch (err) {
            // Silenciar errores de token expirado
            if (err.message !== "Token expirado") {
                console.error('Error al cargar notificaciones:', err);
            }
        }
    };

    const cargarPasesPendientes = async () => {
        try {
            const response = await pasesBandejaCountJWT();
            if (response?.ok) {
                setPasesPendientes(response.count || 0);
            }
        } catch (err) {
            if (err.message !== "Token expirado") {
                console.error('Error al cargar pases pendientes:', err);
            }
        }
    };

    const handleBandejaOpen = () => setBandejaOpen(true);
    const handleBandejaClose = () => {
        setBandejaOpen(false);
        cargarPasesPendientes();
    };

    const handleUserMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleUserMenuClose = () => setAnchorEl(null);

    const handlePerfilClick = () => {
        handleUserMenuClose();
        navigate("/perfil");
    };

    const handleNotifOpen = () => {
        setNotifOpen(true);
    };

    const handleNotifClose = () => {
        setNotifOpen(false);
        cargarNoLeidas(); // Actualizar contador al cerrar
    };

    return (
        <>
            <AppBar position="fixed" className={classes.appBar} elevation={2}>
                <Toolbar>
                    {/* Botón para esconder/mostrar menú lateral */}
                    <Tooltip title="Alternar menú" arrow>
                        <IconButton
                            color="inherit"
                            aria-label="toggle drawer"
                            edge="start"
                            onClick={onDrawerToggle}
                            style={{ marginRight: 16 }}
                            size="large">
                            <MenuIcon />
                        </IconButton>
                    </Tooltip>

                    {/* Título SIGIF */}
                    <Typography variant="h2" className={classes.title} noWrap>
                        Sistema Integral de Gestión Informática Fiscalía de Estado
                    </Typography>

                    {/* Toggle modo oscuro — oculto por ahora, la infra está lista
                        pero requiere repaso de componentes con colores hardcoded */}

                    {/* Mis Tareas (aprobaciones como director/autoridad) */}
                    {tareasCount > 0 && (
                        <Tooltip title="Mis tareas pendientes" arrow>
                            <IconButton
                                color="inherit"
                                className={classes.iconButton}
                                onClick={() => setTareasOpen(true)}
                                size="large">
                                <Badge badgeContent={tareasCount} color="error">
                                    <AssignmentTurnedInIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Bandeja de entrada (pases) */}
                    <Tooltip title="Bandeja de entrada" arrow>
                        <IconButton
                            color="inherit"
                            className={classes.iconButton}
                            onClick={handleBandejaOpen}
                            size="large">
                            <Badge badgeContent={pasesPendientes} color="secondary">
                                <InboxIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    {/* Notificaciones */}
                    <Tooltip title="Notificaciones" arrow>
                        <IconButton
                            color="inherit"
                            className={classes.iconButton}
                            onClick={handleNotifOpen}
                            size="large">
                            <Badge badgeContent={noLeidas} color="secondary">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    {/* Feedback Beta — visible para todos. Admins ven badge con
                        cantidad de reportes no leídos. */}
                    <Tooltip title={esFeedbackAdmin ? `Reportar comentario (${feedbackNoLeidos} sin leer)` : 'Reportar comentario'} arrow>
                        <IconButton
                            color="inherit"
                            className={classes.iconButton}
                            onClick={() => setFeedbackOpen(true)}
                            size="large">
                            <Badge badgeContent={esFeedbackAdmin ? feedbackNoLeidos : 0} color="secondary">
                                <FeedbackIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    {/* Avatar y nombre de usuario con menú */}
                    <div className={classes.usuario} onClick={handleUserMenuOpen}>
                        <Avatar
                            src={!avatarError && avatarSrc ? avatarSrc : undefined}
                            className={classes.avatar}
                            onError={() => setAvatarError(true)}
                        >
                            {(avatarError || !avatarSrc) && getInitials(usuario?.nombre)}
                        </Avatar>
                        <ArrowDropDownIcon />
                    </div>
                <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleUserMenuClose}
                >
                    <MenuItem disabled>
                        <strong>{usuario && capitalizeWords(usuario.nombre)}</strong>
                    </MenuItem>
                    <MenuItem onClick={handlePerfilClick}>
                        <AccountCircleIcon fontSize="small" style={{ marginRight: 8 }} />
                        Perfil
                    </MenuItem>
                    {esAbogado && (
                        <MenuItem onClick={() => { handleUserMenuClose(); navigate('/mi-actividad'); }}>
                            <TimelineIcon fontSize="small" style={{ marginRight: 8 }} />
                            Mi Actividad
                        </MenuItem>
                    )}
                    {puedePersonal && (
                        <MenuItem onClick={() => { handleUserMenuClose(); window.open('/personal', '_blank'); }}>
                            <PeopleIcon fontSize="small" style={{ marginRight: 8 }} />
                            Recursos Humanos
                        </MenuItem>
                    )}
                    {puedeAdminPermisos && (
                        <MenuItem onClick={() => { handleUserMenuClose(); window.open('/admin', '_blank'); }}>
                            <AdminPanelSettingsIcon fontSize="small" style={{ marginRight: 8 }} />
                            Administración
                        </MenuItem>
                    )}
                    {esFeedbackAdmin && (
                        <MenuItem onClick={() => { handleUserMenuClose(); window.open('/feedback-beta', '_blank', 'noopener,noreferrer'); }}>
                            <RateReviewIcon fontSize="small" style={{ marginRight: 8 }} />
                            Panel de Feedback Beta
                            {feedbackNoLeidos > 0 && (
                                <Badge badgeContent={feedbackNoLeidos} color="secondary" sx={{ ml: 'auto', '& .MuiBadge-badge': { position: 'static', transform: 'none' } }} />
                            )}
                        </MenuItem>
                    )}
                    <Divider />
                    <MenuItem
                        onClick={() => {
                            handleUserMenuClose();
                            onLogout();
                        }}
                    >
                        <ExitToAppIcon fontSize="small" style={{ marginRight: 8 }} />
                        Cerrar sesión
                    </MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
            {/* Panel de notificaciones */}
            <NotificacionesPanel open={notifOpen} onClose={handleNotifClose} />
            {/* Dialog de feedback beta */}
            <FeedbackBetaDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)}
                onSuccess={() => { if (esFeedbackAdmin) cargarFeedbackNoLeidos(); }} />
            {/* Panel de bandeja de entrada */}
            <BandejaEntradaPanel open={bandejaOpen} onClose={handleBandejaClose} />
            {/* Panel de Mis Tareas (aprobaciones) */}
            <MisTareasPanel
                open={tareasOpen}
                onClose={() => { setTareasOpen(false); cargarTareas(); }}
                onChange={cargarTareas}
            />
        </>
    );
};

export default Header;
